import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

import { TranslatePipe } from '@core/i18n-legacy';
import { htmlToJson, RtSchema } from '@core/prosemirror-legacy';
import { RenderDirective } from '@core/prosemirror-legacy/angular';
import {
  ProsemirrorRichConfig,
  ProsemirrorRichConfigDirective,
} from '@core/prosemirror-legacy/webapp';
import {
  BadgeComponent,
  ButtonComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  PlaceloaderComponent,
} from '@core/user-interface-legacy';

export interface PositionDescriptionAiResponse {
  content: string;
  prompt?: string;
}

@Component({
  selector: 'tlc-position-description-insert-ai-message',
  templateUrl: './position-description-insert-ai-message.component.html',
  styleUrls: ['./position-description-insert-ai-message.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BadgeComponent,
    ButtonComponent,
    IconButtonComponent,
    IconComponent,
    InputComponent,
    PlaceloaderComponent,
    ProsemirrorRichConfigDirective,
    ReactiveFormsModule,
    RenderDirective,
    TranslatePipe,
  ],
})
export class PositionDescriptionInsertAiMessageComponent {
  private readonly fb = inject(FormBuilder);

  public readonly response = input<PositionDescriptionAiResponse | null>(null);
  public readonly isLoading = input<boolean>(false);

  public readonly close = output<void>();
  public readonly generate = output<string>();
  public readonly insert = output<string>();
  public readonly replace = output<string>();
  public readonly improve = output<string>();

  public readonly focused = signal(false);
  public readonly improveFocused = signal(false);

  public readonly responseValue = computed(() => this.response());
  public readonly contentJson = computed(() => {
    const content = this.response()?.content;
    return content ? htmlToJson(content, RtSchema.PositionDescription) : null;
  });

  public readonly ProsemirrorRichConfig = ProsemirrorRichConfig;

  public readonly form = this.fb.nonNullable.group({
    prompt: ['', Validators.required],
  });

  public readonly improveForm = this.fb.nonNullable.group({
    prompt: [''],
  });

  protected get promptControl(): FormControl<string> {
    return this.form.controls.prompt;
  }

  protected get improvePromptControl(): FormControl<string> {
    return this.improveForm.controls.prompt;
  }

  protected onCloseClick(): void {
    this.close.emit();
  }

  protected onGenerateClicked(): void {
    if (this.promptControl.value) {
      this.generate.emit(this.promptControl.value);
    }
  }

  protected onInsertClicked(): void {
    const content = this.responseValue()?.content;
    if (content) {
      this.insert.emit(content);
    }
  }

  protected onReplaceClicked(): void {
    const content = this.responseValue()?.content;
    if (content) {
      this.replace.emit(content);
    }
  }

  protected onImproveClicked(): void {
    if (this.improvePromptControl.value) {
      this.improve.emit(this.improvePromptControl.value);
      this.improveForm.reset();
    }
  }
}
